import { useTranslation } from 'react-i18next';
interface PaginationProps {
  page: number; lastPage: number; total: number; perPage: number;
  onPageChange: (page: number) => void;
  // Optional — when given, a "rows per page" picker is shown.
  pageSizeOptions?: number[];
  onPerPageChange?: (perPage: number) => void;
}

// 1 … 4 5 [6] 7 8 … 20 — always first/last, a window around the current page.
function pageWindow(page: number, lastPage: number): (number | 'gap-l' | 'gap-r')[] {
  if (lastPage <= 7) return Array.from({ length: lastPage }, (_, i) => i + 1);
  const out: (number | 'gap-l' | 'gap-r')[] = [1];
  const from = Math.max(2, page - 1);
  const to = Math.min(lastPage - 1, page + 1);
  if (from > 2) out.push('gap-l');
  for (let p = from; p <= to; p++) out.push(p);
  if (to < lastPage - 1) out.push('gap-r');
  out.push(lastPage);
  return out;
}

export function Pagination({ page, lastPage, total, perPage, onPageChange, pageSizeOptions, onPerPageChange }: PaginationProps) {
  const { t } = useTranslation();
  const start = total === 0 ? 0 : (page - 1) * perPage + 1;
  const end = Math.min(page * perPage, total);
  const btn = 'min-w-[2rem] px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <p className="text-sm text-gray-500">{t('common.showingRange', { start, end, total })}</p>
        {pageSizeOptions && onPerPageChange && (
          <select value={perPage} onChange={e => onPerPageChange(Number(e.target.value))}
            className="border border-gray-300 rounded px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-brand-400">
            {pageSizeOptions.map(n => <option key={n} value={n}>{n} / page</option>)}
          </select>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(1)} disabled={page <= 1} className={btn} aria-label="First page">«</button>
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1} className={btn} aria-label="Previous page">‹</button>
        {pageWindow(page, lastPage).map(p => typeof p === 'string' ? (
          <span key={p} className="px-1 text-gray-400 select-none">…</span>
        ) : (
          <button key={p} onClick={() => onPageChange(p)} aria-current={p === page ? 'page' : undefined}
            className={"min-w-[2rem] px-3 py-1 text-sm rounded border " + (p === page ? 'bg-brand-600 text-white border-brand-600' : 'border-gray-300 hover:bg-gray-50')}>{p}</button>
        ))}
        <button onClick={() => onPageChange(page + 1)} disabled={page >= lastPage} className={btn} aria-label="Next page">›</button>
        <button onClick={() => onPageChange(lastPage)} disabled={page >= lastPage} className={btn} aria-label="Last page">»</button>
      </div>
    </div>
  );
}
